import { DataModelAttribute, DataModelAttributeTypeName } from '@/types/DataModel';

import { MassUpdateAction, MassUpdateAttributeValue } from './mass-update.types';

export const isDimensionalAttribute = (attribute: Pick<DataModelAttribute, 'typeName'>): boolean =>
    attribute.typeName === DataModelAttributeTypeName.Dimensional;

export const isBlankDimensionalValue = (value: unknown): boolean =>
    value === undefined || value === null || value === '';

export const isPlainMap = (value: unknown): value is Record<string, unknown> =>
    !!value && typeof value === 'object' && !Array.isArray(value);

export type SanitizeDimensionalMassUpdateValueArgs = {
    value: unknown;
    userSelectedKeys: string[];
    activeKey?: string;
};

/**
 * Mass-update editors register the first dimensional key with an empty string as
 * soon as they mount, and React Hook Form may mark that key dirty. That empty
 * default must not be sent — mergeMaps would wipe it on every item.
 *
 * Only keys the user actually targeted are included:
 * - the key visible when they continue
 * - keys whose value they changed (typed, cleared, or typed-then-deleted)
 * - any key that still has a non-blank value
 *
 * Empty targeted keys become null so the backend clears that key only.
 * Visiting a language in the dropdown is not enough.
 */
export const sanitizeDimensionalMassUpdateValue = ({
    value,
    userSelectedKeys,
    activeKey,
}: SanitizeDimensionalMassUpdateValueArgs): Record<string, unknown> | null => {
    const map = isPlainMap(value) ? value : {};
    const keys = new Set<string>(userSelectedKeys);

    if (activeKey) {
        keys.add(activeKey);
    }

    Object.entries(map).forEach(([key, entry]) => {
        if (!isBlankDimensionalValue(entry)) {
            keys.add(key);
        }
    });

    if (keys.size === 0) {
        return null;
    }

    const result: Record<string, unknown> = {};
    keys.forEach((key) => {
        const entry = map[key];
        result[key] = isBlankDimensionalValue(entry) ? null : entry;
    });

    return result;
};

export const isMassUpdateValueCleared = (value: unknown): boolean => {
    if (isBlankDimensionalValue(value)) {
        return true;
    }

    if (Array.isArray(value)) {
        return value.length === 0;
    }

    if (isPlainMap(value)) {
        const entries = Object.values(value);
        return entries.length === 0 || entries.every(isBlankDimensionalValue);
    }

    return false;
};

export const dimensionalKeysFromItemValue = (value: unknown): string[] =>
    isPlainMap(value) ? Object.keys(value) : [];

export const buildMassUpdateItemData = ({
    selectedAttributes,
    updatedValues,
    formValues,
    userSelectedKeysByAttribute,
    activeKeyByAttribute,
}: {
    selectedAttributes: Array<Pick<DataModelAttribute, 'name' | 'typeName'>>;
    updatedValues: Record<string, MassUpdateAttributeValue>;
    formValues: Record<string, unknown>;
    userSelectedKeysByAttribute: Record<string, string[]>;
    activeKeyByAttribute: Record<string, string>;
}): Record<string, MassUpdateAttributeValue> => {
    const itemData: Record<string, MassUpdateAttributeValue> = {};

    selectedAttributes.forEach((attribute) => {
        const current = updatedValues[attribute.name] ?? { action: MassUpdateAction.REPLACE };
        const action = current.action ?? MassUpdateAction.REPLACE;

        if (action === MassUpdateAction.CLEAR) {
            itemData[attribute.name] = { action, value: null };
            return;
        }

        if (isDimensionalAttribute(attribute)) {
            itemData[attribute.name] = {
                action,
                value: sanitizeDimensionalMassUpdateValue({
                    value: formValues[attribute.name],
                    userSelectedKeys: userSelectedKeysByAttribute[attribute.name] ?? [],
                    activeKey: activeKeyByAttribute[attribute.name],
                }),
            };
            return;
        }

        itemData[attribute.name] = {
            action,
            value: formValues[attribute.name] ?? null,
        };
    });

    return itemData;
};
